/**
 * Page Driver Step Component
 *
 * Renders the Page Driver configuration step within the Page Setup Wizard.
 * A page can configure driver defaults for more than one dataset source (Galaxy, HCTR, …); each
 * configured dataset gets its own tab. The trailing "+ Add Source" tab (shown only when the deck
 * permits datasets the page hasn't configured yet) opens a menu of the remaining sources. A page keeps
 * at least one source; any other can be deleted via the link below its driver display, unless
 * decklines on the page still use it.
 */

import { DATASET_DEFAULT_PAGE_DRIVERS } from '@constants/page';
import { useAurigaContext } from '@context/AurigaContext';
import { usePageSetupContext } from '@context/PageSetupContext';
import Alert from '@mui/material/Alert';
import { DatasetSource } from '@services/api/deckManagement/graphql/schema/API';
import { resolveDatasetSourceLabel } from '@utils/enumLabelHelpers';
import { useCallback } from 'react';

import DatasetDriverSetsEditor from './DatasetDriverSetsEditor';

const defaultsFor = (dataset: DatasetSource) => DATASET_DEFAULT_PAGE_DRIVERS[dataset];

// A page must keep at least one dataset.
const canDelete = (_dataset: DatasetSource, configuredDatasets: DatasetSource[]) => configuredDatasets.length > 1;

const PageDriverStep = () => {
  const { pageDriverSets, setPageDriverSets, enabledDatasets, decklineDatasetCounts, pageError } =
    usePageSetupContext();
  const { toastNotification } = useAurigaContext();

  /** Blocks removing a dataset that decklines on the page still use: they would be orphaned. */
  const deleteGuard = useCallback(
    (dataset: DatasetSource) => {
      const dependentCount = decklineDatasetCounts[dataset] ?? 0;
      if (dependentCount === 0) return null;
      return `Can't remove the ${resolveDatasetSourceLabel(dataset)} source: ${dependentCount} deckline${
        dependentCount === 1 ? '' : 's'
      } on this page still use ${dependentCount === 1 ? 'it' : 'them'}. Reassign or remove ${
        dependentCount === 1 ? 'that deckline' : 'those decklines'
      } first.`;
    },
    [decklineDatasetCounts],
  );

  const handleDeleteBlocked = useCallback(
    (reason: string) => toastNotification({ message: reason, type: 'error', visible: true }),
    [toastNotification],
  );

  /** If there is an error, display it instead of the form */
  if (pageError) {
    return <Alert severity='error'>{pageError}</Alert>;
  }

  return (
    <DatasetDriverSetsEditor
      driverSets={pageDriverSets}
      onChange={setPageDriverSets}
      enabledDatasets={enabledDatasets}
      defaultsFor={defaultsFor}
      canDelete={canDelete}
      deleteGuard={deleteGuard}
      onDeleteBlocked={handleDeleteBlocked}
      deleteLabel='Delete Source from Page Driver'
    />
  );
};

export default PageDriverStep;
